import { API_CONFIG } from '../config';
import { guardService } from '../utils/serviceToggles';

const SERVICE_REQUESTS_BASE_URL = import.meta.env.VITE_SERVICE_REQUESTS_API_BASE_URL
  || `${API_CONFIG.TRISERVE_API_URL}/api/service-requests`;

const USE_MOCK = (import.meta.env.VITE_SERVICE_REQUESTS_API_USE_MOCK || 'false') === 'true';

// --------------- Mock helpers ---------------

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

let mockSequence = 4;

const mockRequests = [
  {
    id: 'SR-000001',
    accountNumber: 'TSE-9012-3456',
    category: 'Meter Issue',
    priority: 'Normal',
    description: 'Digital meter display is blank. Power is on but meter shows no reading.',
    serviceAddress: { street: '1234 Oak Street', city: 'Charlotte', state: 'NC', postalCode: '28205' },
    contactPhone: '(555) 123-4567',
    contactEmail: 'jane.smith@toscademos.com',
    preferredDate: '',
    status: 'In Progress',
    createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 86400000).toISOString(),
  },
  {
    id: 'SR-000002',
    accountNumber: 'TSE-9012-3456',
    category: 'Tree Trimming',
    priority: 'Low',
    description: 'Large oak branch growing into power lines along the back fence.',
    serviceAddress: { street: '1234 Oak Street', city: 'Charlotte', state: 'NC', postalCode: '28205' },
    contactPhone: '(555) 123-4567',
    contactEmail: 'jane.smith@toscademos.com',
    preferredDate: '',
    status: 'Open',
    createdAt: new Date(Date.now() - 1 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 86400000).toISOString(),
  },
  {
    id: 'SR-000003',
    accountNumber: 'TSE-9012-3456',
    category: 'Rate Plan Change',
    priority: 'Normal',
    description: 'Requesting switch from Standard Residential to Time-of-Use rate plan.',
    serviceAddress: { street: '1234 Oak Street', city: 'Charlotte', state: 'NC', postalCode: '28205' },
    contactPhone: '(555) 123-4567',
    contactEmail: 'jane.smith@toscademos.com',
    preferredDate: '',
    status: 'Completed',
    createdAt: new Date(Date.now() - 30 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 25 * 86400000).toISOString(),
  },
  {
    id: 'SR-000004',
    accountNumber: 'TSE-5678-1234',
    category: 'Power Outage',
    priority: 'Emergency',
    description: 'Complete power loss to entire property. Neighbors also affected.',
    serviceAddress: { street: '890 Pine Ave', city: 'Raleigh', state: 'NC', postalCode: '27601' },
    contactPhone: '(555) 987-6543',
    contactEmail: 'john.doe@toscademos.com',
    preferredDate: '',
    status: 'Open',
    createdAt: new Date(Date.now() - 2 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 86400000).toISOString(),
  },
  {
    id: 'SR-000005',
    accountNumber: 'TSE-5678-1234',
    category: 'Voltage Problem',
    priority: 'High',
    description: 'Frequent voltage fluctuations causing appliance damage.',
    serviceAddress: { street: '890 Pine Ave', city: 'Raleigh', state: 'NC', postalCode: '27601' },
    contactPhone: '(555) 987-6543',
    contactEmail: 'john.doe@toscademos.com',
    preferredDate: '2026-03-15',
    status: 'In Progress',
    createdAt: new Date(Date.now() - 10 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 86400000).toISOString(),
  },
  {
    id: 'SR-000006',
    accountNumber: 'TSE-3333-7777',
    category: 'Line Repair',
    priority: 'High',
    description: 'Downed power line on south side of property after storm.',
    serviceAddress: { street: '456 Elm Blvd', city: 'Durham', state: 'NC', postalCode: '27701' },
    contactPhone: '(555) 555-0199',
    contactEmail: 'mary.jones@toscademos.com',
    preferredDate: '',
    status: 'Cancelled',
    createdAt: new Date(Date.now() - 45 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 40 * 86400000).toISOString(),
  },
  {
    id: 'SR-000007',
    accountNumber: 'TSE-3333-7777',
    category: 'Start Service',
    priority: 'Normal',
    description: 'New tenant requesting service activation at this address.',
    serviceAddress: { street: '456 Elm Blvd', city: 'Durham', state: 'NC', postalCode: '27701' },
    contactPhone: '(555) 555-0199',
    contactEmail: 'mary.jones@toscademos.com',
    preferredDate: '2026-04-01',
    status: 'Open',
    createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 86400000).toISOString(),
  },
];

// --------------- Public API ---------------

export async function submitServiceRequest(data) {
  guardService('serviceRequests', 'Service Requests API');
  if (USE_MOCK) {
    await wait(800);

    if (!data.accountNumber || !data.category || !data.description) {
      return { success: false, error: 'Missing required fields' };
    }

    const now = new Date().toISOString();
    const newRequest = {
      id: `SR-${String(mockSequence++).padStart(6, '0')}`,
      ...data,
      status: 'Open',
      createdAt: now,
      updatedAt: now,
    };
    mockRequests.unshift(newRequest);
    return { success: true, request: newRequest };
  }

  const res = await fetch(`${SERVICE_REQUESTS_BASE_URL}/requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Submit failed: ${res.status}`);
  return res.json();
}

export async function getServiceRequests(accountNumber) {
  guardService('serviceRequests', 'Service Requests API');
  if (USE_MOCK) {
    await wait(400);

    if (!accountNumber) {
      return { success: false, error: 'accountNumber is required' };
    }

    const filtered = mockRequests.filter((r) => r.accountNumber === accountNumber);
    return { success: true, requests: filtered };
  }

  const params = new URLSearchParams({ accountNumber });
  const res = await fetch(`${SERVICE_REQUESTS_BASE_URL}/requests?${params.toString()}`);
  if (!res.ok) throw new Error(`List failed: ${res.status}`);
  return res.json();
}

export async function getServiceRequestById(id) {
  guardService('serviceRequests', 'Service Requests API');
  if (USE_MOCK) {
    await wait(300);
    const found = mockRequests.find((r) => r.id === id);
    if (!found) return { success: false, error: 'Service request not found' };
    return { success: true, request: found };
  }

  const res = await fetch(`${SERVICE_REQUESTS_BASE_URL}/requests/${id}`);
  if (!res.ok) throw new Error(`Get failed: ${res.status}`);
  return res.json();
}

// --------------- Work Orders API ---------------

export async function getAllServiceRequests(filters = {}) {
  guardService('serviceRequests', 'Service Requests API');
  if (USE_MOCK) {
    await wait(400);
    let results = [...mockRequests];
    if (filters.status) results = results.filter((r) => r.status === filters.status);
    if (filters.priority) results = results.filter((r) => r.priority === filters.priority);
    if (filters.category) results = results.filter((r) => r.category === filters.category);
    return { success: true, requests: results };
  }

  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.category) params.set('category', filters.category);

  const qs = params.toString();
  const url = `${SERVICE_REQUESTS_BASE_URL}/requests${qs ? `?${qs}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`List all failed: ${res.status}`);
  return res.json();
}

export async function updateServiceRequest(id, data) {
  guardService('serviceRequests', 'Service Requests API');
  if (USE_MOCK) {
    await wait(400);
    const idx = mockRequests.findIndex((r) => r.id === id);
    if (idx === -1) return { success: false, error: 'Service request not found' };
    if (data.priority) mockRequests[idx].priority = data.priority;
    if (data.status) mockRequests[idx].status = data.status;
    mockRequests[idx].updatedAt = new Date().toISOString();
    return { success: true, request: { ...mockRequests[idx] } };
  }

  const res = await fetch(`${SERVICE_REQUESTS_BASE_URL}/requests/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Update failed: ${res.status}`);
  return res.json();
}

export async function deleteServiceRequest(id) {
  guardService('serviceRequests', 'Service Requests API');
  if (USE_MOCK) {
    await wait(300);
    const idx = mockRequests.findIndex((r) => r.id === id);
    if (idx === -1) return { success: false, error: 'Service request not found' };
    mockRequests.splice(idx, 1);
    return { success: true };
  }

  const res = await fetch(`${SERVICE_REQUESTS_BASE_URL}/requests/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
  return res.json();
}
